import React, { useEffect, useMemo, useState } from "react";
import { compareSchoolYearDesc } from "../../../../util/schoolYear";
import { useTranslation } from "react-i18next";
import useLessons from "../../../../hooks/useLessons";
import useSettingEvalItems from "../../../../hooks/useSettingEvalItems";
import useLessonPlans from "../../../../hooks/useLessonPlans";
import { useAuthContext } from "../../../../context/AuthContext";
import { useUIContext } from "../../../../context/UIContext";
import styles from "./CopyLessonDrawer.module.css";

const initialPreview = {
  evalItemCount: null,
  hasLessonPlan: null,
};

export default function CopyLessonDrawer({
  settingData,
  termPeriodData,
  schoolId,
  initialSchoolYearId,
  onLoad,
  onClose,
}) {
  const { t } = useTranslation();
  const { user, hasPermission } = useAuthContext();
  const { handleToastCenterTop } = useUIContext();

  const {
    lessonsQuery: { isLoading: isLessonsLoading, data: lessonsData },
  } = useLessons({ schoolid: schoolId });

  // conditions 없이 호출해 전체 평가항목 조회는 막고, 수업별 조회 mutation만 쓴다
  const { getEvalItemsByLessonId } = useSettingEvalItems();
  const { fetchLessonPlanById } = useLessonPlans();

  const [schoolYearId, setSchoolYearId] = useState(
    initialSchoolYearId ? initialSchoolYearId.toString() : "",
  );
  const [termId, setTermId] = useState("");
  const [sortKey, setSortKey] = useState("hierarchy");
  const [selectedLessonId, setSelectedLessonId] = useState("");
  const [copyOptions, setCopyOptions] = useState({
    lessonPlan: false,
    survey: false,
  });
  const [preview, setPreview] = useState(initialPreview);

  const uniqueSchoolYears = useMemo(() => {
    if (!settingData || settingData.length === 0) return [];
    return settingData
      .filter((s) => s.hierarchy_level === 0 && s.parent_id === 0)
      .sort((a, b) => compareSchoolYearDesc(a.school_year, b.school_year));
  }, [settingData]);

  useEffect(() => {
    if (uniqueSchoolYears.length > 0 && !schoolYearId)
      setSchoolYearId(uniqueSchoolYears[0].id.toString());
  }, [uniqueSchoolYears, schoolYearId]);

  // 학기가 과정별로 따로 설정된 경우가 있어 어떤 과정의 학기인지 함께 보여준다
  const filteredTerms = useMemo(() => {
    if (!termPeriodData || !schoolYearId) return [];

    const getHierarchyOneName = (hierarchyId) => {
      if (!hierarchyId || hierarchyId.toString() === "0") return "";
      const found = settingData?.find(
        (s) => s.id?.toString() === hierarchyId.toString(),
      );
      return found?.group_name || "";
    };

    return termPeriodData
      .filter((term) => term.school_year_id.toString() === schoolYearId)
      .map((term) => ({
        ...term,
        hierarchyOneName: getHierarchyOneName(term.applied_hierarchy_one),
      }))
      .sort((a, b) => String(a.start_date).localeCompare(String(b.start_date)));
  }, [termPeriodData, schoolYearId, settingData]);

  useEffect(() => {
    setTermId(filteredTerms.length > 0 ? filteredTerms[0].id.toString() : "");
  }, [filteredTerms]);

  // 목록 권한은 수업생성 페이지와 동일한 정책을 따른다
  const filteredLessons = useMemo(() => {
    if (!lessonsData || !schoolYearId) return [];

    let filtered = lessonsData.filter(
      (lesson) => lesson.school_year_id?.toString() === schoolYearId,
    );

    if (termId)
      filtered = filtered.filter(
        (lesson) => lesson.term_id?.toString() === termId,
      );

    if (!hasPermission("", "can_read"))
      filtered = filtered.filter((lesson) =>
        lesson.teachers?.some(
          (teacher) =>
            teacher?.username?.toLowerCase() === user?.username?.toLowerCase(),
        ),
      );

    return filtered;
  }, [lessonsData, schoolYearId, termId, hasPermission, user?.username]);

  const sortedLessons = useMemo(() => {
    const compareName = (a, b) =>
      String(a.subject_name).localeCompare(String(b.subject_name));
    const list = [...filteredLessons];

    if (sortKey === "nameAsc") return list.sort(compareName);
    if (sortKey === "nameDesc") return list.sort((a, b) => compareName(b, a));

    return list.sort(
      (a, b) =>
        String(a.hierarchyOne).localeCompare(String(b.hierarchyOne)) ||
        String(a.hierarchyTwo).localeCompare(String(b.hierarchyTwo)) ||
        String(a.hierarchyThree).localeCompare(String(b.hierarchyThree)) ||
        compareName(a, b),
    );
  }, [filteredLessons, sortKey]);

  const selectedLesson = useMemo(
    () =>
      sortedLessons.find((lesson) => lesson.id.toString() === selectedLessonId),
    [sortedLessons, selectedLessonId],
  );

  // 계획서가 없는 수업이면 체크를 유지하지 않는다
  useEffect(() => {
    if (preview.hasLessonPlan === false)
      setCopyOptions((prev) => ({ ...prev, lessonPlan: false }));
  }, [preview.hasLessonPlan]);

  const handleSchoolYearChange = (e) => {
    setSchoolYearId(e.target.value);
    setSelectedLessonId("");
    setPreview(initialPreview);
  };

  const handleTermChange = (e) => {
    setTermId(e.target.value);
    setSelectedLessonId("");
    setPreview(initialPreview);
  };

  // 선택한 수업의 부속물 개수를 조회해서 체크박스 의미를 명확히 한다
  const handleSelect = (lesson) => {
    setSelectedLessonId(lesson.id.toString());
    setPreview(initialPreview);

    getEvalItemsByLessonId.mutate(
      { schoolid: schoolId, lesson_id: lesson.id },
      {
        onSuccess: (data) =>
          setPreview((prev) => ({
            ...prev,
            evalItemCount: Array.isArray(data) ? data.length : 0,
          })),
        onError: () => setPreview((prev) => ({ ...prev, evalItemCount: 0 })),
      },
    );

    fetchLessonPlanById(lesson.id, { schoolid: schoolId })
      .then((rawData) => {
        const lessonPlan = rawData?.data || rawData;
        setPreview((prev) => ({ ...prev, hasLessonPlan: !!lessonPlan?.id }));
      })
      .catch(() => setPreview((prev) => ({ ...prev, hasLessonPlan: false })));
  };

  const handleOptionChange = (e) => {
    const { name, checked } = e.target;
    setCopyOptions((prev) => ({ ...prev, [name]: checked }));
  };

  const handleLoad = () => {
    if (!selectedLesson) {
      handleToastCenterTop(
        t("teacher.lessonRelated.copySelectLesson") ||
          "복사할 수업을 선택해주세요.",
      );
      return;
    }
    onLoad(selectedLesson, copyOptions);
  };

  return (
    <div className={styles.drawer}>
      <div className={styles.header}>
        <h5 className={styles.title}>
          {t("teacher.lessonRelated.copyLesson") || "수업 복사"}
        </h5>
        <button type="button" className={styles.closeButton} onClick={onClose}>
          ✕
        </button>
      </div>

      <div className={styles.body}>
        <div className={styles.guide}>
          {t("teacher.lessonRelated.copyLessonGuide") ||
            "원본 수업의 수업명, 시수, 평가제, 평가항목을 불러옵니다. 과정·학년·반·학기·교사는 현재 선택한 값이 그대로 유지됩니다. 불러온 뒤 수정해서 저장하면 새 수업이 생성됩니다."}
        </div>

        <div className={styles.filters}>
          <div className={styles.filterItem}>
            <label>{t("common.labels.schoolYear") || "학년도"}</label>
            <select
              className={styles.select}
              value={schoolYearId}
              onChange={handleSchoolYearChange}
            >
              {uniqueSchoolYears.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.school_year}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.filterItem}>
            <label>{t("common.labels.termName") || "학기"}</label>
            <select
              className={styles.select}
              value={termId}
              onChange={handleTermChange}
            >
              {filteredTerms.map((term) => (
                <option key={term.id} value={term.id}>
                  {term.term_name}
                  {term.hierarchyOneName ? `(${term.hierarchyOneName})` : ""}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className={styles.listHeader}>
          <span>
            {t("teacher.lessonRelated.copyLessonListHeader") ||
              "수업 목록 (선택하여 불러오기)"}
          </span>
          <select
            className={styles.sortSelect}
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value)}
          >
            <option value="hierarchy">
              {t("teacher.lessonRelated.copySortHierarchy") || "과정순"}
            </option>
            <option value="nameAsc">
              {t("teacher.lessonRelated.copySortNameAsc") || "수업명 ↑"}
            </option>
            <option value="nameDesc">
              {t("teacher.lessonRelated.copySortNameDesc") || "수업명 ↓"}
            </option>
          </select>
        </div>
        <div className={styles.list}>
          {sortedLessons.map((lesson) => (
            <div
              key={lesson.id}
              className={`${styles.item} ${
                selectedLessonId === lesson.id.toString() ? styles.selected : ""
              }`}
              onClick={() => handleSelect(lesson)}
            >
              <span className={styles.itemSubject}>{lesson.subject_name}</span>
              <span className={styles.itemInfo}>
                {[
                  lesson.hierarchyOne,
                  lesson.hierarchyTwo,
                  lesson.hierarchyThree,
                ]
                  .filter(Boolean)
                  .join("-")}{" "}
                | {lesson.teachers?.map((teacher) => teacher.tname).join(", ")}{" "}
                | {lesson.evaluation_type}
              </span>
            </div>
          ))}
          {sortedLessons.length === 0 && (
            <div className={styles.noLessons}>
              {isLessonsLoading
                ? `${t("common.status.loading") || "불러오는 중"}..`
                : t("teacher.lessonRelated.copyNoLessons") ||
                  "해당 조건의 수업이 없습니다."}
            </div>
          )}
        </div>

        {selectedLesson && (
          <div className={styles.preview}>
            <div className={styles.previewRow}>
              <span>{t("teacher.lessonRelated.evalItem") || "평가항목"}</span>
              <strong>
                {preview.evalItemCount === null
                  ? t("common.status.loading") || "확인 중.."
                  : `${preview.evalItemCount}${
                      t("common.labels.countUnit") || "개"
                    }`}
              </strong>
            </div>
            <div className={styles.previewRow}>
              <span>
                {t("teacher.lessonRelated.copyLessonPlan") || "수업계획서"}
              </span>
              <strong>
                {preview.hasLessonPlan === null
                  ? t("common.status.loading") || "확인 중.."
                  : preview.hasLessonPlan
                    ? t("common.labels.exist") || "있음"
                    : t("common.labels.notExist") || "없음"}
              </strong>
            </div>
          </div>
        )}

        <div className={styles.optionsBox}>
          <div className={styles.optionsTitle}>
            {t("teacher.lessonRelated.copyOptions") || "같이 복사할 항목"}
          </div>
          <div className={styles.checkRow}>
            <input
              type="checkbox"
              id="copyLessonPlanOption"
              name="lessonPlan"
              checked={copyOptions.lessonPlan}
              disabled={preview.hasLessonPlan === false}
              onChange={handleOptionChange}
            />
            <label htmlFor="copyLessonPlanOption">
              {t("teacher.lessonRelated.copyLessonPlan") || "수업계획서"}
              {preview.hasLessonPlan === false &&
                ` (${t("teacher.lessonRelated.copyNoPlan") || "원본에 없음"})`}
            </label>
          </div>
          <div className={styles.checkRow}>
            <input
              type="checkbox"
              id="copySurveyOption"
              name="survey"
              checked={copyOptions.survey}
              onChange={handleOptionChange}
            />
            <label htmlFor="copySurveyOption">
              {t("teacher.lessonRelated.copySurveyQuestions") ||
                "설문 교사 추가질문"}
            </label>
          </div>
          <div className={styles.alwaysCopied}>
            {t("teacher.lessonRelated.copyEvalItemsNotice") ||
              "평가항목(루브릭·평가계획서 포함)은 기본으로 복사됩니다. 복사된 설문은 비공개 상태로 시작합니다."}
          </div>
        </div>
      </div>

      <div className={styles.footer}>
        <button
          type="button"
          className={styles.loadButton}
          onClick={handleLoad}
          disabled={!selectedLesson}
        >
          {t("teacher.lessonRelated.copyLoad") || "불러오기"}
        </button>
        <button type="button" className={styles.cancelButton} onClick={onClose}>
          {t("common.labels.cancel") || "취소"}
        </button>
      </div>
    </div>
  );
}
